import { MouseEvent, useState, useEffect } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  CreateMemberActionResponse,
  createMemberAction,
  member_field_options,
  selectCreateMemberResult,
} from './memberSlice';
import { Member } from '../../types';

type MemberForm = Required<Pick<Member, 
  |'username'|'description'|'state'
>> & {
  member_name: string;
  password: string;
  password_options: 'empty'|'custom';
}
const initialState: MemberForm = {
  member_name: '',
  username: '',
  password: '',
  description: '',
  state: 'enabled',
  password_options: 'empty',
};

type Props = {
  tenant_id: string;
  successCallback: (result: CreateMemberActionResponse) => void;
  dismissCallback: () => void;
};

export function MemberCreateForm({ tenant_id, successCallback, dismissCallback }: Props) {
  const dispatch = useAppDispatch();
  const create_result = useAppSelector(selectCreateMemberResult);
  const [api_error, setApiError] = useState<SerializedError|undefined>();
  const { handleSubmit, register, watch, formState: { errors } } = useForm({
    defaultValues: initialState,
  });
  const password_options = watch('password_options');
  const show_password_input = password_options === 'custom';

  useEffect(() => {
    if (create_result) {
      if (createMemberAction.fulfilled.match(create_result)) {
        successCallback(create_result.payload);
      }
      if (createMemberAction.rejected.match(create_result)) {
        setApiError(create_result.error);
        const subscription = watch(() => {
          setApiError(undefined);
          subscription.unsubscribe();
        });
      }
    }
  }, [create_result, successCallback, watch]);

  const handleCreate = async(data: MemberForm) => {
    let password;
    if (data.username) {
      if (data.password_options === 'custom') {
        password = data.password;
      }
    }
    dispatch(createMemberAction({
      tenant_id,
      body: {
        ...data,
        name: data.member_name,
        password,
      },
    }));
  };
  const handleCancel = (event: MouseEvent) => {
    event.preventDefault();
    dismissCallback();
  };
  return (
    <form onSubmit={handleSubmit(handleCreate)}>
      <fieldset>
        <div className="mb-3 row">
          <label htmlFor="member_name" className="col-sm-3 col-form-label">Name</label>
          <div className="col-sm-6">
            <input type="text" id="member_name" className="form-control"
              { ...register('member_name', member_field_options.name) }
              name="member_name"
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.member_name?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="username" className="col-sm-3 col-form-label">Username</label>
          <div className="col-sm-6">
            <input type="text" id="username" className="form-control"
              { ...register('username', member_field_options.username) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.username?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="member_description" className="col-sm-3 col-form-label">Description</label>
          <div className="col-sm-6">
            <textarea id="member_description" className="form-control"
              { ...register('description', member_field_options.description) }
            >
            </textarea>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.description?.message}</span>
        </div>
        <div className="mb-3 row">
          <label className="col-sm-3 col-form-label">Password</label>
          <div className="mt-2 col-sm-6">
            <div className="form-check form-check-inline">
              <input className="form-check-input" type="radio"
                id="empty_password" value="empty"
                { ...register('password_options', { required: true }) }
              />
              <label className="form-check-label" htmlFor="empty_password">
                None
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input className="form-check-input" type="radio"
                id="custom_password" value="custom"
                { ...register('password_options', { required: true }) }
              />
              <label className="form-check-label" htmlFor="custom_password">
                Custom
              </label>
            </div>
            {show_password_input && <>
            <div className="mt-2">
              <input type="password" id="password" className="form-control" autoComplete="new-password"
                { ...register('password', member_field_options.password) }
              />
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.password?.message}</span>
            </>}
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="member_state" className="col-sm-3 col-form-label">State</label>
          <div className="col-sm-6">
            <select id="member_state" className="form-select"
              { ...register('state') }
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
        <div className="d-flex">
          <span className="text-danger">{api_error?.message ?? api_error?.name}</span>
        </div>
        <div className="d-flex my-3 justify-content-center">
          <button type="submit" className="btn btn-primary mx-3">Create</button>
          <button className="btn btn-light mx-1" onClick={(e) => handleCancel(e)}>Cancel</button>
        </div>
      </fieldset>
    </form>
  );
}
